import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { loadShipyard, upgradeShipyard } from '../api/shipyardApi';
import { useOptionalAppSession } from '../hooks/AppSessionContext';

const DEFAULT_ASSETS = {
  sound: 'https://app.pfandpirat.com/wp-content/uploads/freibeuterhafen-sound.mp3',
  head: 'https://app.pfandpirat.com/wp-content/uploads/werft-head.avif',
  overview: 'https://app.pfandpirat.com/wp-content/uploads/werft.mp4',
  info: 'https://app.pfandpirat.com/wp-content/uploads/werft-infos.avif',
};

const CURRENCY_ASSETS = {
  gold: 'https://app.pfandpirat.com/wp-content/uploads/Goldmuenze.png',
  silver: 'https://app.pfandpirat.com/wp-content/uploads/silbermuenze.png',
  bronze: 'https://app.pfandpirat.com/wp-content/uploads/bronzemuenze.avif',
};

function getCurrencyAsset(tier) {
  return CURRENCY_ASSETS[tier] || '';
}

function splitWallet(wallet) {
  const balance = wallet?.balance || {};
  return {
    gold: Number(balance.gold || 0),
    silver: Number(balance.silver || 0),
    bronze: Number(balance.bronze || 0),
  };
}

function RequirementCard({ requirement }) {
  const isCurrency = requirement.type === 'currency';
  const currencyTier = requirement.currency_tier || 'mixed';
  const currencyIconUrl = isCurrency ? (requirement.icon_url || getCurrencyAsset(currencyTier)) : '';
  const currencyIcons = { mixed: '🪙' };
  const title = isCurrency ? (requirement.name || requirement.cost_label || 'Münzen') : requirement.name;
  const owned = requirement.display_owned ?? Math.min(Number(requirement.owned || 0), Number(requirement.required || 0));
  const required = requirement.display_required ?? Number(requirement.required || 0);
  const missing = requirement.display_missing ?? Number(requirement.missing || 0);

  return (
    <article className={`pfp-shipyard-requirement ${requirement.met ? 'is-met' : 'is-missing'} ${isCurrency ? `is-currency is-${currencyTier}` : ''}`}>
      <strong>{title}</strong>
      {currencyIconUrl ? <img src={currencyIconUrl} alt="" loading="lazy" /> : requirement.icon_url ? <img src={requirement.icon_url} alt="" loading="lazy" /> : <span className="pfp-shipyard-currency-icon">{currencyIcons[currencyTier] || '🪙'}</span>}
      <div className="pfp-shipyard-count"><span>{owned}</span><em>/</em><span>{required}</span></div>
      <small>{requirement.met ? 'Bereit' : `Noch benötigt: ${missing}`}</small>
    </article>
  );
}

function WalletCoin({ tier, amount, label }) {
  const iconUrl = getCurrencyAsset(tier);
  return (
    <span>
      {iconUrl ? <img className="pfp-shipyard-wallet-coin" src={iconUrl} alt="" loading="lazy" /> : null}
      <strong>{amount}</strong>
      <small>{label}</small>
    </span>
  );
}

function ResourceCard({ resource }) {
  return (
    <article className={`pfp-shipyard-resource is-${resource.rarity || 'common'}`}>
      <strong>{resource.name}</strong>
      {resource.icon_url ? <img src={resource.icon_url} alt="" loading="lazy" /> : <span>📦</span>}
      <b>{Number(resource.quantity || 0)}</b>
      <small>Im Besitz</small>
    </article>
  );
}

function ShipyardHarborMasterCta({ ship, onOpen }) {
  const shipLevel = Number(ship?.level || 0);
  const unlocked = Boolean(ship?.max_level || shipLevel >= 5);

  return (
    <section className={`pfp-shipyard-card pfp-shipyard-harbormaster-cta ${unlocked ? 'is-unlocked' : 'is-locked'}`}>
      <div>
        <span>Hafenmeister</span>
        <h2>{unlocked ? 'Hafen-Duelle freigeschaltet' : 'Hafen-Duelle ab Stufe 5'}</h2>
        <p>
          {unlocked
            ? 'Dein Schiff ist bereit. Anmeldungen, Gegner, Kampfberichte und Beute laufen jetzt beim Hafenmeister.'
            : 'Baue dein Schiff weiter aus. Sobald es kampfbereit ist, ruft der Hafenmeister neue Duelle und Hafen-Events aus.'}
        </p>
      </div>
      <button type="button" onClick={onOpen}>{unlocked ? 'Zum Hafenmeister' : 'Hafenmeister ansehen'}</button>
    </section>
  );
}

export default function ShipyardPage() {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const sessionContext = useOptionalAppSession();
  const bootstrapShipyard = sessionContext?.session?.shipyard || null;
  const hasBootstrapShipyard = Boolean(bootstrapShipyard);
  const mergeSessionPatch = sessionContext?.mergeSessionPatch;
  const [state, setState] = useState(() => bootstrapShipyard);
  const [loading, setLoading] = useState(!hasBootstrapShipyard);
  const [error, setError] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(false);

  const assets = useMemo(() => ({ ...DEFAULT_ASSETS, ...(state?.assets || {}) }), [state]);
  const wallet = splitWallet(state?.wallet);
  const ship = state?.ship || {};
  const next = state?.next_blueprint || null;
  const current = state?.current_blueprint || null;
  const progress = Math.max(0, Math.min(100, Number(ship.progress_percent || 0)));
  const hasShip = Boolean(ship.has_ship ?? Number(ship.level || 0) > 0);
  const shipName = hasShip ? (ship.name || 'Dein Schiff') : 'Noch kein Schiff gebaut';
  const shipLevel = Number(ship.level ?? 0);
  const shipStatus = hasShip ? (ship.max_level ? 'Maximal ausgebaut' : 'Noch im Bau') : 'Noch nicht gebaut';
  const shipBonusLabels = ship.bonus_labels || current?.bonus_labels || (hasShip ? ['+2 % Chance auf Ressourcenfund'] : ['0 % Ressourcenfund-Bonus', '0 % Lootchance-Bonus']);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const payload = await loadShipyard();
      setState(payload);
      if (mergeSessionPatch) {
        mergeSessionPatch({ shipyard: payload, wallet: { wallet: payload?.wallet || null } });
      }
    } catch (loadError) {
      setError(loadError);
    } finally {
      setLoading(false);
    }
  }, [mergeSessionPatch]);

  useEffect(() => {
    if (hasBootstrapShipyard) {
      setState(bootstrapShipyard);
      setLoading(false);
    } else {
      refresh();
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [bootstrapShipyard, hasBootstrapShipyard, refresh]);

  const toggleSound = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (soundEnabled) {
      audio.pause();
      setSoundEnabled(false);
      return;
    }
    try {
      audio.volume = 0.32;
      audio.loop = true;
      await audio.play();
      setSoundEnabled(true);
    } catch (err) {
      setNotice({ type: 'error', title: 'Sound blockiert', text: 'Bitte tippe den Sound-Button erneut an.' });
    }
  }, [soundEnabled]);

  const handleUpgrade = useCallback(async () => {
    if (!state?.can_upgrade || upgrading) return;
    setUpgrading(true);
    try {
      const payload = await upgradeShipyard();
      setState(payload);
      if (mergeSessionPatch) {
        mergeSessionPatch({ shipyard: payload, wallet: { wallet: payload?.wallet || null } });
      }
      setNotice({ type: 'success', title: `Upgrade abgeschlossen: ${payload?.upgraded?.name || 'Neues Schiff'}`, text: 'Dein Schiff wurde erfolgreich ausgebaut und die Vorteile sind jetzt aktiv.' });
    } catch (upgradeError) {
      setNotice({ type: 'error', title: 'Upgrade nicht möglich', text: upgradeError.message || 'Es fehlen noch Ressourcen.' });
      await refresh();
    } finally {
      setUpgrading(false);
    }
  }, [refresh, mergeSessionPatch, state?.can_upgrade, upgrading]);

  if (loading) {
    return <div className="pfp-shipyard-page"><div className="pfp-shipyard-loading">⚓ Werft wird geöffnet...</div></div>;
  }

  if (error) {
    return <div className="pfp-shipyard-page"><div className="pfp-shipyard-error"><h2>Werft konnte nicht geladen werden</h2><p>{error.message}</p><button type="button" onClick={refresh}>Erneut versuchen</button></div></div>;
  }

  return (
    <div className="pfp-shipyard-page">
      <Helmet><title>Werft – Pfandpirat</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <audio ref={audioRef} src={assets.sound} preload="none" loop />
      <div className="pfp-shipyard-shell">
        <div className="pfp-shipyard-topbar">
          <button type="button" className="pfp-shipyard-back" onClick={() => navigate('/freibeuterhafen')}>← Zurück zum Hafen</button>
          <button type="button" className="pfp-shipyard-sound" onClick={toggleSound}>{soundEnabled ? '🔊 Hafensound läuft' : '🔈 Hafensound starten'}</button>
        </div>

        <div className="pfp-shipyard-head-row">
          <img className="pfp-shipyard-head" src={assets.head} alt="Werft – Baue dein eigenes Schiff" />
        </div>

        <section className="pfp-shipyard-hero">
          <div className="pfp-shipyard-wallet"><WalletCoin tier="gold" amount={wallet.gold} label="Gold" /><WalletCoin tier="silver" amount={wallet.silver} label="Silber" /><span>🟤 <strong>{wallet.bronze}</strong><small>Bronze</small></span></div>
          <video className="pfp-shipyard-overview pfp-shipyard-overview-video" autoPlay muted loop playsInline preload="metadata" poster="https://app.pfandpirat.com/wp-content/uploads/Werft-Ueberblick.png" aria-label="Werft am Meer mit Schiff im Bau"><source src={assets.overview} type="video/mp4" /></video>
          <aside className="pfp-shipyard-hint">🏴‍☠️ Jedes Upgrade macht dein Schiff stärker und bringt dir Vorteile auf deinen Abenteuern.</aside>
        </section>

        <main className="pfp-shipyard-card pfp-shipyard-main-card">
          <div className="pfp-shipyard-current"><div><span>Dein Schiff</span><h1>{shipName}</h1><b>Stufe {shipLevel}</b><p>{shipStatus}</p><small>Aktueller Bonus</small><ul>{shipBonusLabels.map((label) => <li key={label}>☘️ {label}</li>)}</ul></div>{hasShip && ship.image_url ? <img src={ship.image_url} alt={ship.name} /> : null}</div>
          <aside className="pfp-shipyard-progress-card"><strong>Baufortschritt</strong><b>{progress}%</b><div className="pfp-shipyard-progress"><span style={{ width: `${progress}%` }} /></div><p>{next ? `Nächster Meilenstein: ${next.name}` : 'Dein Schiff ist vollständig ausgebaut.'}</p><small>{next ? `Noch ${Math.max(0, 100 - progress)}% bis zum nächsten Upgrade` : 'Alle aktuell verfügbaren Bauphasen abgeschlossen.'}</small></aside>

          {next ? <section className="pfp-shipyard-next"><h2>Benötigt für nächstes Upgrade: {next.name} (Stufe {next.level})</h2><div className="pfp-shipyard-requirements">{(next.requirements || []).map((req) => <RequirementCard key={`${req.type}-${req.slug}`} requirement={req} />)}</div><aside className="pfp-shipyard-reward-card"><h3>Belohnungen</h3><ul>{(next.bonus_labels || []).map((label) => <li key={label}>⭐ {label}</li>)}</ul></aside><button type="button" className={`pfp-shipyard-upgrade ${state?.can_upgrade ? 'is-ready' : ''}`} disabled={!state?.can_upgrade || upgrading} onClick={handleUpgrade}>{state?.can_upgrade ? (upgrading ? 'Wird ausgebaut...' : 'Upgrade durchführen') : 'Ressourcen fehlen'}<small>{state?.can_upgrade ? 'Alle Anforderungen erfüllt' : 'Sammle die benötigten Materialien, um dein Schiff weiterzubauen.'}</small></button><button type="button" className="pfp-shipyard-preview" onClick={() => setNotice({ type: 'info', title: next.name, text: next.description })}>👁 Zur Vorschau</button></section> : <section className="pfp-shipyard-next is-complete"><h2>Deine Flotte ist bereit</h2><p>Alle aktuell verfügbaren Werft-Upgrades sind abgeschlossen.</p></section>}
        </main>

        <section className="pfp-shipyard-card pfp-shipyard-resources"><h2>Deine Werft-Ressourcen</h2><div className="pfp-shipyard-resource-grid">{(state?.resources || []).map((resource) => <ResourceCard key={resource.slug} resource={resource} />)}</div><div className="pfp-shipyard-resource-footer"><span>ⓘ Tipp: Werft-Ressourcen findest du später auf Expeditionen, im Markt oder als Belohnung für Aufträge.</span><button type="button" onClick={() => navigate('/freibeuterhafen')}>🏪 Zum Markt</button></div></section>

        <ShipyardHarborMasterCta ship={ship} onOpen={() => navigate('/freibeuterhafen/hafenmeister')} />

        <section className="pfp-shipyard-info"><div className="pfp-shipyard-info-art"><img src={assets.info} alt="Werft-Infos" /><div className="pfp-shipyard-info-copy"><p>Baue dein Schiff Stück für Stück aus und schalte neue Vorteile frei.<br />Je stärker dein Schiff, desto größer dein Erfolg auf hoher See!</p></div></div><div className="pfp-shipyard-next-mini"><span>Nächstes Upgrade</span><strong>{next?.name || 'Maximalstufe'}</strong>{next?.image_url ? <img src={next.image_url} alt="" /> : null}</div></section>
      </div>

      {notice ? <div className={`pfp-shipyard-notice is-${notice.type || 'info'}`} role="dialog" aria-live="polite"><button type="button" onClick={() => setNotice(null)}>×</button><h3>{notice.title}</h3><p>{notice.text}</p></div> : null}
    </div>
  );
}
